import { Edit, TrashCan } from '@carbon/icons-react';
import {
  Button,
  ComboBox,
  InlineNotification,
  Modal,
  Select,
  SelectItem,
  SkeletonText,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  TextInput,
  Toggle,
} from '@carbon/react';
import { asProblem, problemMessage, RowAction } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  deleteProfile,
  listAllPages,
  listProfiles,
  profilesKey,
  saveProfile,
  scopeApis,
  type DeliveryProfile,
  type DeliveryProvider,
  type ProviderAccount,
} from './deliveryApi';

const EVERYONE = '';

type Draft = {
  id?: string;
  name: string;
  /** `brand:{id}`, `store:{id}` or empty for everyone. */
  scope: string;
  accountId: string;
  serviceType: string;
  settings: Record<string, string>;
  isActive: boolean;
};

const blank = (accountId: string): Draft => ({ name: '', scope: EVERYONE, accountId, serviceType: '', settings: {}, isActive: true });

const draftOf = (p: DeliveryProfile): Draft => ({
  id: p.id,
  name: p.name,
  scope: p.storeId ? `store:${p.storeId}` : p.brandId ? `brand:${p.brandId}` : EVERYONE,
  accountId: p.providerAccountId,
  serviceType: p.serviceType ?? '',
  settings: { ...p.settings },
  isActive: p.isActive,
});

/**
 * A provider service's integration profiles (ADR 0112): which of the provider's accounts books it for a brand, a store or
 * everyone, with its own service type and setting values (vehicle, payment method, search time). The store's own profile wins
 * over its brand's, the brand's over everyone's; with none the service's account is used. Credentials stay on the account.
 */
export function ProfilesEditor({
  serviceId,
  provider,
  accounts,
}: {
  serviceId: string;
  provider: DeliveryProvider;
  accounts: ProviderAccount[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const mine = accounts.filter((a) => a.providerId === provider.id);
  const profiles = useQuery({ queryKey: profilesKey(serviceId), queryFn: () => listProfiles(serviceId) });
  const scopes = useQuery({
    queryKey: ['ho', 'delivery', 'profile-scopes'],
    queryFn: async () => ({ brands: await listAllPages(scopeApis.brands), stores: await listAllPages(scopeApis.stores) }),
  });
  const [draft, setDraft] = useState<Draft>(blank(mine[0]?.id ?? ''));
  const [submitted, setSubmitted] = useState(false);
  const [deleting, setDeleting] = useState<DeliveryProfile>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: profilesKey(serviceId) });
  const settings = provider.settings.filter((s) => !s.isSecret);
  const missing = !draft.name.trim() || !draft.accountId;

  const save = useMutation({
    mutationFn: (d: Draft) =>
      saveProfile(serviceId, d.id, {
        name: d.name.trim(),
        brandId: d.scope.startsWith('brand:') ? d.scope.slice(6) : null,
        storeId: d.scope.startsWith('store:') ? d.scope.slice(6) : null,
        providerAccountId: d.accountId,
        serviceType: d.serviceType.trim() || null,
        settings: Object.fromEntries(Object.entries(d.settings).filter(([, v]) => v.trim() !== '')),
        isActive: d.isActive,
      }),
    onSuccess: async () => {
      setDraft(blank(mine[0]?.id ?? ''));
      setSubmitted(false);
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (p: DeliveryProfile) => save.mutateAsync({ ...draftOf(p), isActive: !p.isActive }),
  });
  const remove = useMutation({
    mutationFn: (p: DeliveryProfile) => deleteProfile(serviceId, p.id),
    onSuccess: async () => {
      setDeleting(undefined);
      await refresh();
    },
  });
  const busy = save.isPending || remove.isPending;

  const nameOf = (list: { id: string; name: string }[] | undefined, id: string) =>
    list?.find((x) => x.id === id)?.name ?? t('delivery.services.scope.unknown', { id: id.slice(0, 8) });
  const scopeText = (p: DeliveryProfile) =>
    p.storeId
      ? `${t('delivery.services.scope.store')}: ${nameOf(scopes.data?.stores, p.storeId)}`
      : p.brandId
        ? `${t('delivery.services.scope.brand')}: ${nameOf(scopes.data?.brands, p.brandId)}`
        : t('delivery.services.scope.everywhere');
  const detailText = (p: DeliveryProfile) =>
    [mine.find((a) => a.id === p.providerAccountId)?.name ?? t('delivery.services.profiles.accountGone'), p.serviceType].filter(Boolean).join(' · ');

  return (
    <section className="pos-delivery__assignments" aria-label={t('delivery.services.profiles.title')}>
      <h3 className="pos-delivery__subheading">{t('delivery.services.profiles.title')}</h3>
      <p className="pos-delivery__hint">{t('delivery.services.profiles.hint')}</p>
      {profiles.isLoading && <SkeletonText paragraph lineCount={2} />}
      {profiles.error && (
        <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(profiles.error))} />
      )}
      {(profiles.data ?? []).length > 0 && (
        <StructuredListWrapper isCondensed aria-label={t('delivery.services.profiles.title')}>
          <StructuredListBody>
            {(profiles.data ?? []).map((p) => (
              <StructuredListRow key={p.id}>
                <StructuredListCell>{p.name}</StructuredListCell>
                <StructuredListCell>{scopeText(p)}</StructuredListCell>
                <StructuredListCell>{detailText(p)}</StructuredListCell>
                <StructuredListCell>
                  <Toggle
                    id={`profile-${p.id}`}
                    hideLabel
                    labelText={p.name}
                    labelA={t('delivery.services.assignments.off')}
                    labelB={t('delivery.services.assignments.on')}
                    toggled={p.isActive}
                    disabled={busy}
                    onToggle={() => toggle.mutate(p)}
                  />
                </StructuredListCell>
                <StructuredListCell>
                  <RowAction action={t('admin.edit')} record={p.name} icon={Edit} onClick={() => setDraft(draftOf(p))} />
                  <RowAction
                    action={t('common.delete')}
                    record={p.name}
                    icon={TrashCan}
                    onClick={() => {
                      remove.reset();
                      setDeleting(p);
                    }}
                  />
                </StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
      )}
      {profiles.data?.length === 0 && <p className="pos-delivery__hint">{t('delivery.services.profiles.none')}</p>}

      <h4 className="pos-delivery__hint">{draft.id ? t('delivery.services.profiles.editing', { name: draft.name }) : t('delivery.services.profiles.add')}</h4>
      <div className="pos-delivery__scope">
        <TextInput
          id="profile-name"
          labelText={t('delivery.services.profiles.name')}
          value={draft.name}
          maxLength={100}
          invalid={submitted && !draft.name.trim()}
          invalidText={t('admin.validation.required')}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
        <Select
          id="profile-scope"
          labelText={t('delivery.services.profiles.scope')}
          value={draft.scope}
          onChange={(e) => setDraft((d) => ({ ...d, scope: e.target.value }))}
        >
          <SelectItem value={EVERYONE} text={t('delivery.services.scope.everywhere')} />
          {(scopes.data?.brands ?? []).map((b) => (
            <SelectItem key={b.id} value={`brand:${b.id}`} text={`${t('delivery.services.scope.brand')}: ${b.name}`} />
          ))}
          {(scopes.data?.stores ?? []).map((s) => (
            <SelectItem key={s.id} value={`store:${s.id}`} text={`${t('delivery.services.scope.store')}: ${s.name}`} />
          ))}
        </Select>
        <Select
          id="profile-account"
          labelText={t('delivery.services.profiles.account')}
          value={draft.accountId}
          invalid={submitted && !draft.accountId}
          invalidText={t('delivery.services.profiles.noAccount')}
          onChange={(e) => setDraft((d) => ({ ...d, accountId: e.target.value }))}
        >
          {mine.map((a) => (
            <SelectItem key={a.id} value={a.id} text={a.name} />
          ))}
        </Select>
        <ComboBox
          id="profile-service-type"
          titleText={t('delivery.services.profiles.serviceType')}
          helperText={t('delivery.services.profiles.serviceTypeHelp')}
          allowCustomValue
          items={provider.serviceTypes.map((s) => s.code)}
          itemToString={(code) => (code ? (provider.serviceTypes.find((s) => s.code === code)?.name ?? code) : '')}
          selectedItem={draft.serviceType || null}
          onChange={({ selectedItem, inputValue }) => setDraft((d) => ({ ...d, serviceType: selectedItem ?? inputValue ?? '' }))}
        />
      </div>
      {settings.length > 0 && (
        <>
          <p className="pos-delivery__hint">{t('delivery.services.profiles.settingsHint')}</p>
          <div className="pos-delivery__scope">
            {settings.map((s) => {
              const label = t(s.labelKey as I18nKey, { defaultValue: s.key });
              const value = draft.settings[s.key] ?? '';
              const set = (next: string) => setDraft((d) => ({ ...d, settings: { ...d.settings, [s.key]: next } }));
              return s.type === 'choice' || s.type === 'boolean' ? (
                <Select key={s.key} id={`profile-setting-${s.key}`} labelText={label} value={value} onChange={(e) => set(e.target.value)}>
                  <SelectItem value="" text={t('delivery.services.profiles.accountValue')} />
                  {(s.type === 'boolean' ? ['true', 'false'] : (s.options ?? [])).map((o) => (
                    <SelectItem key={o} value={o} text={o} />
                  ))}
                </Select>
              ) : (
                <TextInput
                  key={s.key}
                  id={`profile-setting-${s.key}`}
                  labelText={label}
                  placeholder={t('delivery.services.profiles.accountValue')}
                  value={value}
                  maxLength={500}
                  onChange={(e) => set(e.target.value)}
                />
              );
            })}
          </div>
        </>
      )}
      <div className="pos-delivery__test">
        <Button
          kind="tertiary"
          disabled={busy}
          onClick={() => {
            setSubmitted(true);
            if (!missing) save.mutate(draft);
          }}
        >
          {draft.id ? t('delivery.services.profiles.save') : t('delivery.services.profiles.add')}
        </Button>
      </div>
      {save.error && <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(save.error))} />}
      {deleting && (
        <Modal
          open
          danger
          preventCloseOnClickOutside
          modalHeading={t('delivery.services.profiles.deleteTitle')}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('common.delete')}
          secondaryButtonText={t('common.cancel')}
          primaryButtonDisabled={remove.isPending}
          onRequestClose={() => setDeleting(undefined)}
          onRequestSubmit={() => remove.mutate(deleting)}
        >
          <p>{t('delivery.services.profiles.deleteBody', { name: deleting.name })}</p>
          {remove.error && (
            <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(remove.error))} />
          )}
        </Modal>
      )}
    </section>
  );
}
